/**
 * Hook centralizado para gerenciar subscriptions realtime do sistema
 * Otimização v2.1: Consolidado de 12 → 4 channels WebSocket
 *
 * Saúde da conexão (2026-09-14): vem do PRÓPRIO socket, não de um ping.
 * O desenho anterior (herdado do Supabase) mandava um broadcast "ping" a cada
 * 5 min e esperava ouvi-lo de volta — mas o canal do Socket.io não tem
 * `send` (TypeError no console a cada 5 min) e o servidor nunca devolveu
 * broadcast, então o "pong" nunca chegava: toda aba se declarava
 * "disconnected" a cada 15 min e fazia `refetchQueries({type:'active'})`
 * inteiro, sem nada ter caído. O Socket.io já tem heartbeat de engine e
 * reconexão automática; o que a tela precisa saber — caiu, voltou — ele
 * conta pelos eventos connect / disconnect / connect_error.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime } from '@/lib/realtime';
import { useNetworkStatus } from './useNetworkStatus';
import { useProductionCoreSync } from './realtime/useProductionCoreSync';
import { useCommerceLogisticsSync } from './realtime/useCommerceLogisticsSync';
import { useConfigSync } from './realtime/useConfigSync';

export const useRealtimeSync = () => {
  const queryClient = useQueryClient();
  const lastSuccessfulSyncRef = useRef<Date>(new Date());
  
  const reconnectAttemptsRef = useRef(0);
  const isOnline = useNetworkStatus();

  // Estado de saúde da conexão
  const [connectionHealth, setConnectionHealth] = useState<'healthy' | 'stale' | 'disconnected'>('healthy');

  // Callback para atualizar lastSync quando eventos chegam
  const updateLastSync = useCallback(() => {
    lastSuccessfulSyncRef.current = new Date();
    setConnectionHealth('healthy');
    reconnectAttemptsRef.current = 0;
  }, []);

  // ✅ CONSOLIDADO: 3 hooks em vez de 7
  // NOTA: useChatSync permanece em App.tsx (requer AuthContext)
  useProductionCoreSync();      // orders + steps + events + observations
  useCommerceLogisticsSync();   // arte_jobs + logistics (6 tabelas)
  useConfigSync();              // intelligence config (4 tabelas)
  
  // Saúde da conexão pelo socket: um canal só para isto (o mesmo custo do
  // antigo "heartbeat-check"), ouvindo os status que o adaptador já reporta.
  //   SUBSCRIBED  → conectou (ou RECONECTOU): saudável; se tinha caído, o que
  //                 aconteceu no intervalo não chegou — invalida uma vez, e só
  //                 nesse caso (nunca refetch periódico: CLAUDE.md §7.5).
  //   CLOSED      → caiu; o Socket.io tenta voltar sozinho: "stale".
  //   erro/timeout → não consegue conectar: "disconnected".
  useEffect(() => {
    const canal = realtime.channel('heartbeat-check');
    let caiu = false;
    canal.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        if (caiu) {
          caiu = false;
          console.log('🔌 Realtime voltou — invalidando o que ficou para trás');
          queryClient.invalidateQueries();
        }
        reconnectAttemptsRef.current = 0;
        lastSuccessfulSyncRef.current = new Date();
        setConnectionHealth('healthy');
        return;
      }
      caiu = true;
      reconnectAttemptsRef.current += 1;
      setConnectionHealth(status === 'CLOSED' ? 'stale' : 'disconnected');
    });
    return () => {
      realtime.removeChannel(canal);
    };
  }, [queryClient]);

  useEffect(() => {
    console.log('🚀 Realtime Sync v2.1: Sistema consolidado (4 channels, saúde pelo socket)');
    lastSuccessfulSyncRef.current = new Date();
    setConnectionHealth('healthy');

    return () => {
      console.log('🔕 Realtime Sync: Limpando timers');
    };
  }, []);

  // Network recovery
  useEffect(() => {
    if (isOnline && connectionHealth === 'disconnected') {
      // O socket reconecta sozinho e invalida ao voltar (efeito acima); aqui
      // só o que a rede pede — sem forçar refetch (CLAUDE.md §7.5).
      console.log('🌐 Rede recuperada');
      queryClient.invalidateQueries();
      setConnectionHealth('healthy');
      lastSuccessfulSyncRef.current = new Date();
    }
  }, [isOnline, queryClient, connectionHealth]);

  return {
    connectionHealth,
    isConnected: connectionHealth === 'healthy',
    lastSync: lastSuccessfulSyncRef.current,
    isOnline,
    reconnectAttempts: reconnectAttemptsRef.current,
    updateLastSync
  };
};
